import { GRID_WIDTH, GRID_HEIGHT } from "../core/config.ts";
import { COLORS } from "../core/constants.ts";
import { CELL_SIZE } from "../core/config.ts";
import { canvasFont } from "./canvasType.ts";
import { getEpochColors } from "./EpochTheme.ts";
import { isDominion } from "./CorruptionTheme.ts";
import { ECHELON } from "./designTokens.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";

/** S126: glyphs her label-glitch swaps in (render-only flavor). */
const TAINT_GLYPHS = "ΣΞ#%&01";

const TWO_PI = Math.PI * 2;
/** The clean ring's angular speed: one full turn per 3000 ms (the pre-CR rate). */
const RING_BASE_RATE = TWO_PI / 3000;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Deterministic 0..1 hash of a number (render-only — no sim/RNG coupling). The
 *  bucketed wall-clock seeds give the corruption a stable, non-metronomic phase
 *  without a per-frame Math.random (which would jitter every frame). */
function hash01(n: number): number {
  const s = Math.sin(n * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

/** Parse #RRGGBB → [r,g,b]; null on anything else (e.g. a token we don't own). */
function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** CR-8: lerp two hex colours (the mainframe greens its way to her red as it
 *  takes damage). Falls back to the harder endpoint if either side isn't a plain
 *  #RRGGBB, so an unexpected token can never throw mid-render. */
function mixHex(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  if (!ca || !cb) return t >= 0.5 ? b : a;
  const k = clamp01(t);
  const r = Math.round(ca[0] + (cb[0] - ca[0]) * k);
  const g = Math.round(ca[1] + (cb[1] - ca[1]) * k);
  const bl = Math.round(ca[2] + (cb[2] - ca[2]) * k);
  return `rgb(${r},${g},${bl})`;
}

/**
 * S195/BL-234(b) — the edge-cell inboard nudge (world px). Pure + exported so
 * the framing contract is unit-locked: an edge-cell core pulls ~size·0.21
 * inboard, leaving its ~size·0.5 visual reach breathing room to the board
 * edge; interior cells are untouched.
 */
export function coreInboardNudge(
  col: number,
  row: number,
  size: number,
): { x: number; y: number } {
  const n = Math.round(size * 0.21);
  return {
    x: col === GRID_WIDTH - 1 ? -n : col === 0 ? n : 0,
    y: row === GRID_HEIGHT - 1 ? -n : row === 0 ? n : 0,
  };
}

export class MainframeRenderer {
  private time = 0;
  private hp = 20;
  private maxHp = 20;
  private hitFlashTimer = 0;
  private shakeX = 0;
  private shakeY = 0;
  /** CR-8: the ring's rotation is now INTEGRATED (not derived from absolute
   *  time) so a damage-driven, glitching angular velocity — fast, then stalled,
   *  then a hard lurch — reads continuously instead of snapping. */
  private ringAngle = 0;

  setHP(current: number, max: number): void {
    this.hp = current;
    this.maxHp = max;
  }

  /** CR-8: integrity damage 0 (pristine) → 1 (one breach from down). Drives the
   *  green→red shift, the ring destabilisation, and the glitch density. */
  private damage(): number {
    return this.maxHp > 0 ? clamp01(1 - this.hp / this.maxHp) : 0;
  }

  triggerHit(): void {
    this.hitFlashTimer = 200;
    this.shakeX = (Math.random() - 0.5) * 6;
    this.shakeY = (Math.random() - 0.5) * 6;
  }

  update(dt: number): void {
    this.time += dt;

    // CR-8: a wounded mainframe spins WRONG. The clean ring holds a steady rate;
    // as damage climbs, a layered wobble speeds it up / stalls it, and brief
    // hard "stutters" lurch (or kick backward) — more frequent the closer to
    // breach. Reduced motion keeps the steady clean rate (no destabilisation).
    const dmg = this.damage();
    let rate = RING_BASE_RATE;
    if (!isReducedMotion() && dmg > 0) {
      const wobble = 1.5 * Math.sin(this.time / 230) + 0.8 * Math.sin(this.time / 67);
      let mul = 1 + dmg * wobble;
      // Hard stutter: short windows where the spin lurches or reverses. Their
      // odds + violence scale with damage (a dying core can't hold its rotation).
      const bucket = Math.floor(this.time / 520);
      if (dmg > 0.25 && hash01(bucket * 1.7) < 0.18 + dmg * 0.5) {
        const phase = (this.time - bucket * 520) / 520; // 0..1 within the bucket
        if (phase < 0.16) mul += (hash01(bucket * 7.3) - 0.35) * dmg * 10;
      }
      rate = RING_BASE_RATE * mul;
    }
    this.ringAngle += rate * dt;

    if (this.hitFlashTimer > 0) {
      this.hitFlashTimer -= dt;
      if (this.hitFlashTimer <= 0) {
        this.shakeX = 0;
        this.shakeY = 0;
      } else {
        const decay = this.hitFlashTimer / 200;
        this.shakeX *= decay;
        this.shakeY *= decay;
      }
    }
  }

  /** CR-8: the live glitch intensity (0..1) — mostly 0, with brief spikes whose
   *  frequency + height grow with damage. Drives the position jitter + the RGB
   *  chroma-split ghosts. Silent under reduced motion (returns 0). */
  private glitchAmount(dmg: number): number {
    if (isReducedMotion() || dmg <= 0.03) return 0;
    const bucket = Math.floor(this.time / 470);
    if (hash01(bucket * 2.3) > 0.14 + dmg * 0.6) return 0; // no spike this bucket
    const phase = (this.time - bucket * 470) / 470; // 0..1 within the bucket
    const win = phase < 0.24 ? 1 - phase / 0.24 : 0; // a short decaying flash
    return clamp01(win * (0.35 + dmg * 0.65));
  }

  render(
    ctx: CanvasRenderingContext2D,
    col: number,
    row: number,
    wave: number,
    corruption = 0,
  ): void {
    const size = CELL_SIZE;
    const epoch = getEpochColors(wave);
    const hpRatio = this.maxHp > 0 ? this.hp / this.maxHp : 1;
    const dmg = this.damage();
    // CR-8: the whole core greens its way toward her red as integrity falls — a
    // CONTINUOUS shift (not the old binary flip at 30%), so every hit reads.
    const bodyColor = mixHex(epoch.primary, COLORS.ORACLE_RED, dmg * 0.92);
    // CR-8: a brief glitch flash nudges the whole assembly off-register.
    const glitch = this.glitchAmount(dmg);
    const gx = glitch > 0 ? (hash01(this.time) - 0.5) * glitch * 5 : 0;
    const gy = glitch > 0 ? (hash01(this.time * 1.7) - 0.5) * glitch * 3 : 0;
    // S195/BL-234(b) — the exit cell sits on the board's LAST column, so a
    // cell-centred glyph with ~half-cell reach touched the canvas edge by
    // construction ("MAINFRAME" truncated, the core flush against the deploy
    // rail — screenshot-proven in COMBAT_FEEL_AUDIT_S195.md §4). The glyph
    // (and its label) draw nudged inboard along the edge's inward normal;
    // the sim cell never moves — enemies still walk to the true centre.
    const nudge = coreInboardNudge(col, row, size);
    const cx = col * size + size / 2 + nudge.x + this.shakeX + gx;
    const cy = row * size + size / 2 + nudge.y + this.shakeY + gy;
    // S126: structural taint is Act III only ("Dominion") and grows from 0 at
    // the act door to 1 at the wave-35 finale — she reaches the core LAST.
    const taint = isDominion(wave)
      ? Math.max(0, Math.min(1, (corruption - 0.5) * 2))
      : 0;

    ctx.save();

    // Outer hex ring (rotating — CR-8: a damage-glitched angular velocity).
    const ringRadius = size * 0.42;
    const rotation = this.ringAngle;
    // CR-8: RGB chroma-split ghosts under the clean ring during a glitch flash —
    // the cyber-corruption "datamosh" vocabulary the enemy glitch-death uses.
    if (glitch > 0.01) {
      this.drawChromaGhost(ctx, cx, cy, ringRadius, rotation, glitch);
    }
    this.drawHexRing(ctx, cx, cy, ringRadius, rotation, bodyColor);
    if (taint > 0) {
      // One hex segment runs in HER color, wandering the ring (~0.7 s/step —
      // a thin 1.5 px stroke, cell-local; sub-threshold by area + rate).
      this.drawTaintSegment(ctx, cx, cy, ringRadius, rotation, taint);
    }

    // Inner core pulse (CR-8: the core's pulse quickens as it's wounded).
    const pulseRate = 500 - dmg * 250;
    const pulseScale = 1 + Math.sin(this.time / pulseRate) * 0.08;
    const coreRadius = size * 0.18 * pulseScale;
    this.drawCore(ctx, cx, cy, coreRadius, bodyColor, hpRatio);

    // Corner brackets
    this.drawCornerBrackets(ctx, cx, cy, size * 0.36, bodyColor);

    // MAINFRAME micro-label (S126: under dominion she briefly glitch-swaps a
    // character, ~0.3 s every 4 s — "MAINFRΣME" in her color)
    let label = "MAINFRAME";
    let labelColor = bodyColor;
    if (taint > 0 && this.time % 4000 < 300) {
      const bucket = Math.floor(this.time / 4000);
      const pos = (bucket * 7) % label.length;
      label =
        label.slice(0, pos) +
        TAINT_GLYPHS[(bucket * 13) % TAINT_GLYPHS.length] +
        label.slice(pos + 1);
      labelColor = ECHELON.SIGNAL;
    }
    ctx.font = canvasFont("fieldLabel");
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = labelColor;
    ctx.globalAlpha = 0.7;
    ctx.fillText(label, cx, cy + size * 0.34);
    ctx.globalAlpha = 1;

    // S126: late-act veins — her reach closing on the core (intensity ≥ 0.8).
    if (taint >= 0.6) {
      this.drawTaintVeins(ctx, cx, cy, size * 0.46, (taint - 0.6) / 0.4);
    }

    // Damage states
    if (hpRatio <= 0.6) {
      this.drawCracks(ctx, cx, cy, size * 0.3, hpRatio);
    }
    if (hpRatio <= 0.3) {
      this.drawRedFlicker(ctx, cx, cy, size * 0.4);
    }
    if (this.hp <= 1 && this.hp > 0) {
      this.drawSparks(ctx, cx, cy, size * 0.35);
    }

    // Hit flash overlay
    if (this.hitFlashTimer > 0) {
      const alpha = (this.hitFlashTimer / 200) * 0.4;
      ctx.fillStyle = COLORS.ORACLE_RED;
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(cx, cy, size * 0.45, 0, TWO_PI);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    ctx.restore();
  }

  /** S126: one hex-ring segment in ECHELON's color, stepping around the ring
   *  every ~0.7 s — her grip on the core's perimeter. */
  private drawTaintSegment(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    rotation: number,
    taint: number,
  ): void {
    const seg = Math.floor(this.time / 700) % 6;
    const a0 = rotation + (seg / 6) * TWO_PI;
    const a1 = rotation + ((seg + 1) / 6) * TWO_PI;
    ctx.strokeStyle = ECHELON.SIGNAL;
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.35 + 0.4 * taint;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a0) * r, cy + Math.sin(a0) * r);
    ctx.lineTo(cx + Math.cos(a1) * r, cy + Math.sin(a1) * r);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  /** S126: jagged veins reaching inward from the cell edge — her hand closing
   *  on the core in the act's final stretch. Deterministic time-seeded angles
   *  (the drawCracks grammar), slow 6 s pulse, cell-local. */
  private drawTaintVeins(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    reach: number,
  ): void {
    const pulse = 0.8 + 0.2 * Math.sin((this.time / 6000) * TWO_PI);
    ctx.strokeStyle = ECHELON.DEEP;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.45 * reach * pulse;
    const seed = Math.floor(this.time / 3000);
    for (let i = 0; i < 3; i++) {
      const angle = ((seed + i) * 2.39996) % TWO_PI; // golden-angle spread
      const outerR = r;
      const innerR = r * (1 - 0.55 * reach);
      const midR = (outerR + innerR) / 2;
      const kink = Math.sin((seed + i) * 7) * r * 0.12;
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(angle) * outerR, cy + Math.sin(angle) * outerR);
      ctx.lineTo(
        cx + Math.cos(angle) * midR - Math.sin(angle) * kink,
        cy + Math.sin(angle) * midR + Math.cos(angle) * kink,
      );
      ctx.lineTo(cx + Math.cos(angle) * innerR, cy + Math.sin(angle) * innerR);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  /** CR-8: the datamosh under-layer — a cyan + rose hex-ring pulled apart by a
   *  few px (the established enemy glitch-death chroma vocabulary), additively
   *  blended so the corruption reads as the ring tearing off-register. */
  private drawChromaGhost(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    rotation: number,
    g: number,
  ): void {
    const chrom = 1.5 + 4 * g;
    const prevOp = ctx.globalCompositeOperation;
    ctx.globalCompositeOperation = "lighter";
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.5 * g;
    this.hexPath(ctx, cx - chrom, cy, r, rotation);
    ctx.strokeStyle = COLORS.ICE_CYAN;
    ctx.stroke();
    this.hexPath(ctx, cx + chrom, cy, r, rotation);
    ctx.strokeStyle = COLORS.ORACLE_RED;
    ctx.stroke();
    ctx.globalCompositeOperation = prevOp;
    ctx.globalAlpha = 1;
  }

  /** Trace a hex ring (shared by the clean ring + the chroma ghosts). */
  private hexPath(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    rotation: number,
  ): void {
    ctx.beginPath();
    for (let i = 0; i <= 6; i++) {
      const angle = rotation + (i / 6) * TWO_PI;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
  }

  private drawHexRing(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    rotation: number,
    color: string,
  ): void {
    const sides = 6;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.8;
    ctx.beginPath();
    for (let i = 0; i <= sides; i++) {
      const angle = rotation + (i / sides) * TWO_PI;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  private drawCore(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    color: string,
    hpRatio: number,
  ): void {
    // CR-8: `color` already carries the green→red damage shift (mixHex upstream).
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.6 + hpRatio * 0.4;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, TWO_PI);
    ctx.fill();

    // Inner bright point
    ctx.fillStyle = "#ffffff";
    ctx.globalAlpha = 0.5 + Math.sin(this.time / 300) * 0.3;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.3, 0, TWO_PI);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  private drawCornerBrackets(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    offset: number,
    color: string,
  ): void {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.2;
    ctx.globalAlpha = 0.6;
    const len = 6;
    const corners = [
      { x: cx - offset, y: cy - offset, dx: 1, dy: 1 },
      { x: cx + offset, y: cy - offset, dx: -1, dy: 1 },
      { x: cx - offset, y: cy + offset, dx: 1, dy: -1 },
      { x: cx + offset, y: cy + offset, dx: -1, dy: -1 },
    ];
    for (const c of corners) {
      ctx.beginPath();
      ctx.moveTo(c.x + c.dx * len, c.y);
      ctx.lineTo(c.x, c.y);
      ctx.lineTo(c.x, c.y + c.dy * len);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  private drawCracks(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    hpRatio: number,
  ): void {
    const crackIntensity = 1 - hpRatio / 0.6;
    ctx.strokeStyle = COLORS.ORACLE_RED;
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = crackIntensity * 0.6;

    const seed = Math.floor(this.time / 2000);
    for (let i = 0; i < 3; i++) {
      const angle = ((seed + i) * 2.1) % TWO_PI;
      const startR = r * 0.3;
      const endR = r * (0.6 + crackIntensity * 0.4);
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(angle) * startR, cy + Math.sin(angle) * startR);
      ctx.lineTo(cx + Math.cos(angle) * endR, cy + Math.sin(angle) * endR);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  private drawRedFlicker(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
  ): void {
    const flicker = Math.sin(this.time / 100) > 0.3;
    if (!flicker) return;
    ctx.fillStyle = COLORS.ORACLE_RED;
    ctx.globalAlpha = 0.15;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, TWO_PI);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  private drawSparks(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
  ): void {
    const sparkCount = 4;
    ctx.fillStyle = COLORS.AMBER_WARN;
    ctx.globalAlpha = 0.8;
    for (let i = 0; i < sparkCount; i++) {
      const angle = (this.time / 80 + i * 1.5) % TWO_PI;
      const dist = r * (0.5 + Math.sin(this.time / 120 + i) * 0.3);
      const sx = cx + Math.cos(angle) * dist;
      const sy = cy + Math.sin(angle) * dist;
      ctx.beginPath();
      ctx.arc(sx, sy, 1, 0, TWO_PI);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }
}
